/**
 * SiteInsightsModal — Deep-dive popup for a single site
 * ─────────────────────────────────────────────────────
 * Opened from the Analytics map when an operator clicks a marker.
 *
 * Renders:
 *   • Side-by-side Satellite (Leaflet tile screenshot) + Google Street
 *     View header — helps the underwriter visually confirm the site.
 *   • Latest risk assessment: band, computed vs effective score, ABM
 *     cash-exposure line and the full factor breakdown.
 *   • Questionnaire answers grouped by section.
 *   • Score-trend mini-chart when the site has been surveyed 2+ times.
 *   • Photo carousel for native Raptor surveys; historical Excel/PDF
 *     imports render a friendly "no photos available" state.
 *
 * The modal is purely additive — it never mutates the parent Analytics
 * filters, so closing it drops the operator back on the exact same view.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { AuthedImage } from '@/components/AuthedImage';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip,
} from 'recharts';
import { MapContainer, TileLayer, CircleMarker } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  X, MapPin, Satellite, Camera, ShieldAlert, Wallet, TrendingUp,
  ChevronLeft, ChevronRight, ExternalLink, ImageOff, Compass, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useGoogleMaps } from '@/lib/useGoogleMaps';

/* ─── Types ─────────────────────────────────────────────────────────── */

interface MapProviderCfg {
  key: string;
  name: string;
  tile_url: string;
  subdomains: string[];
  max_zoom: number;
  satellite_url?: string;
  satellite_subdomains?: string[];
  satellite_max_zoom?: number;
  satellite_name?: string;
  street_view_available?: boolean;
  street_view_url?: string | null;
  street_view_metadata_url?: string | null;
}

interface SiteInsightsTarget {
  site_id: string;
  site_name: string;
  lat: number;
  lng: number;
  /** Optional band from the map point so we can render a badge instantly
   *  while the full assessment payload loads. */
  band?: string;
  /** Optional score for the same reason. */
  score?: number;
}

interface AssessmentSummary {
  id: string;
  instruction_id: string;
  result_id?: string;
  site_name?: string;
  client_name?: string;
  template_name?: string;
  template_version?: number;
  surveyor_name?: string | null;
  submitted_at?: string | null;
  computed_at?: string;
  normalised_score: number;
  effective_score: number;
  effective_band: string;
  computed_band: string;
  is_overridden: boolean;
  abm_match_confidence?: number | null;
  abm_resolved_model?: string | null;
  abm_unknown_types?: string[];
}

interface Factor {
  factor_id?: string;
  question_code?: string;
  question_text?: string;
  name?: string;
  cluster?: string;
  category?: string;
  answer: string | number | boolean | null;
  weight: number;
  awarded_points?: number;
  max_points?: number;
  contribution?: number;
  passed?: boolean;
  notes?: string;
  is_abm_row?: boolean;
}

interface AssessmentDetail extends AssessmentSummary {
  factors: Factor[];
  breakdown?: {
    cash_value?: {
      cash_value_zar: number | null;
      resolved_model: string | null;
      match_confidence?: number;
      description?: string;
    };
  };
}

interface Photo {
  id: string;
  storage_key?: string;
  width?: number | null;
  height?: number | null;
  source?: string;
  caption?: string;
}

interface SurveyResult {
  id: string;
  instruction_id: string;
  answers?: Record<string, { value: unknown; label?: string; category?: string } | unknown>;
  submitted_at?: string;
}

interface InstructionDetail {
  id: string;
  result?: SurveyResult;
  photos?: Photo[];
}

/* ─── Colour tokens (matches Analytics.tsx BAND_COLOR) ─────────────── */

const BAND_COLOR: Record<string, string> = {
  Low: '#10B981', Medium: '#F59E0B', High: '#EF4444', Critical: '#7F1D1D',
  low: '#10B981', medium: '#F59E0B', high: '#EF4444', critical: '#7F1D1D',
  Unknown: '#6D28D9',
};

/** Pretty-print a band label whatever casing the backend hands us. */
const bandLabel = (b?: string): string =>
  b ? b.charAt(0).toUpperCase() + b.slice(1).toLowerCase() : 'Unknown';

/* ─── The modal ─────────────────────────────────────────────────────── */

export const SiteInsightsModal: React.FC<{
  target: SiteInsightsTarget;
  mapProvider?: MapProviderCfg;
  unsurveyed?: boolean;
  onClose: () => void;
}> = ({ target, mapProvider, unsurveyed, onClose }) => {
  const [assessments, setAssessments] = useState<AssessmentSummary[]>([]);
  const [detail, setDetail] = useState<AssessmentDetail | null>(null);
  const [instruction, setInstruction] = useState<InstructionDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [photoIdx, setPhotoIdx] = useState<number>(0);
  const [failedPhotos, setFailedPhotos] = useState<Set<string>>(new Set());
  const [streetViewOk, setStreetViewOk] = useState<'ok' | 'none' | 'denied' | null>(null);

  /* Escape-to-close ─ standard modal accessibility. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Lock body scroll while the modal is open. */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* Data load — assessments list first (needed for trend + latest id),
     then drill for the detail + instruction payload in parallel. */
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const list = await api.get<AssessmentSummary[]>(
        `/risk/assessments?site_id=${encodeURIComponent(target.site_id)}&limit=100`,
      );
      const rows = list.data || [];
      // Sort newest first so [0] is the "current" assessment.
      rows.sort((a, b) => {
        const da = new Date(a.submitted_at || a.computed_at || 0).getTime();
        const dbb = new Date(b.submitted_at || b.computed_at || 0).getTime();
        return dbb - da;
      });
      setAssessments(rows);
      if (rows.length === 0) return;
      const latest = rows[0];
      const [d, inst] = await Promise.all([
        api.get<AssessmentDetail>(`/risk/assessments/${latest.id}`),
        api.get<InstructionDetail>(`/instructions/${latest.instruction_id}`).catch(() => ({ data: null })),
      ]);
      setDetail(d.data);
      if (inst && inst.data) setInstruction(inst.data as InstructionDetail);
    } catch (err) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
        ?? 'Failed to load site insights.';
      toast.error(msg);
    } finally { setLoading(false); }
    /* `detail` is intentionally not a dep — we set it, not read it. */
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [target.site_id]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  /* Probe Google Street View metadata endpoint so we can hide the
     Street View pane entirely when there's no imagery at these coords —
     AND distinguish "no coverage here" from a key/billing/referrer
     problem so the operator gets an actionable message. */
  useEffect(() => {
    const meta = mapProvider?.street_view_metadata_url;
    if (!meta) { setStreetViewOk(null); return; }
    const url = meta
      .replace('{lat}', String(target.lat))
      .replace('{lng}', String(target.lng));
    fetch(url)
      .then((r) => r.json())
      .then((j) => {
        const status = String(j?.status ?? '');
        if (status === 'OK') setStreetViewOk('ok');
        else if (status === 'ZERO_RESULTS' || status === 'NOT_FOUND') setStreetViewOk('none');
        else setStreetViewOk('denied');  // REQUEST_DENIED / OVER_QUERY_LIMIT / etc.
      })
      .catch(() => setStreetViewOk('denied'));
  }, [mapProvider?.street_view_metadata_url, target.lat, target.lng]);

  /* Photos to render — instruction endpoint returns them alongside the
     survey_result. Historic imports return an empty array which drives
     the friendly empty-state below. */
  const photos: Photo[] = useMemo(
    () => (instruction?.photos ?? []).filter((p) => !!p && !!p.id),
    [instruction?.photos],
  );
  useEffect(() => { setPhotoIdx(0); }, [photos.length]);

  const activePhoto = photos[photoIdx] || null;
  const prevPhoto = (): void => setPhotoIdx((i) => (photos.length === 0 ? 0 : (i - 1 + photos.length) % photos.length));
  const nextPhoto = (): void => setPhotoIdx((i) => (photos.length === 0 ? 0 : (i + 1) % photos.length));

  /* Assessments trend series — only render the chart when there are 2+
     data points to actually plot a line through. */
  const trend = useMemo(() => (
    [...assessments]
      .filter((a) => a.submitted_at || a.computed_at)
      .sort((a, b) => new Date(a.submitted_at || a.computed_at || 0).getTime()
                     - new Date(b.submitted_at || b.computed_at || 0).getTime())
      .map((a) => ({
        date: new Date(a.submitted_at || a.computed_at || 0).toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
        score: Number(a.effective_score?.toFixed?.(1) ?? a.effective_score ?? 0),
        band: bandLabel(a.effective_band),
      }))
  ), [assessments]);

  /* Group factors by cluster/category so the answer list reads like the
     original questionnaire rather than an ordered dump. */
  const factorsByCategory = useMemo(() => {
    const groups: Record<string, Factor[]> = {};
    (detail?.factors || []).forEach((f) => {
      const key = f.cluster || f.category || 'General';
      if (!groups[key]) groups[key] = [];
      groups[key].push(f);
    });
    return groups;
  }, [detail?.factors]);

  /* Human-readable label for a factor row — the backend uses different
     keys for different engines (abm_v3 → question_text, legacy → name).
     We fall back through the chain so no row ever renders blank. */
  const factorLabel = (f: Factor): string =>
    f.question_text || f.name || f.question_code || f.factor_id || 'Question';

  const abmCash = detail?.breakdown?.cash_value;
  const isGoogle = mapProvider?.key === 'google_maps';

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/60 backdrop-blur-sm p-4 md:p-8 overflow-y-auto"
      data-testid="site-insights-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="site-insights-title"
    >
      <div
        className="relative w-full max-w-5xl bg-white rounded-xl shadow-2xl border border-border/70 flex flex-col animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header ─────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-white border-b border-border/70 px-5 py-4 flex items-start justify-between rounded-t-xl">
          <div className="min-w-0 pr-3">
            <div id="site-insights-title" className="flex items-center gap-2 font-headline text-xl text-raptor-navy">
              <MapPin size={20} className="text-raptor-blue shrink-0" />
              <span className="truncate" data-testid="site-insights-name">{target.site_name || 'Unnamed site'}</span>
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-raptor-navy/70 font-body">
              {detail?.client_name && <span className="font-semibold text-raptor-navy/80">{detail.client_name}</span>}
              {detail?.client_name && <span className="opacity-40">·</span>}
              <span className="font-tabular">{target.lat.toFixed(5)}, {target.lng.toFixed(5)}</span>
              {detail?.submitted_at && (<>
                <span className="opacity-40">·</span>
                <span className="font-tabular">Surveyed {new Date(detail.submitted_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' })}</span>
              </>)}
              {(detail?.effective_band || target.band) && (
                <Badge
                  style={{ backgroundColor: BAND_COLOR[detail?.effective_band || target.band || 'Unknown'] || BAND_COLOR.Unknown, color: 'white' }}
                  data-testid="site-insights-band"
                >
                  {unsurveyed && !detail ? 'Not surveyed' : bandLabel(detail?.effective_band || target.band)}
                </Badge>
              )}
            </div>
          </div>
          <Button size="icon" variant="ghost" onClick={onClose} data-testid="site-insights-close" aria-label="Close">
            <X size={18} />
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-raptor-navy/60" data-testid="site-insights-loading">
            <Loader2 className="animate-spin" size={18} /> Loading site insights…
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Side-by-side imagery row — ALWAYS shown so an un-surveyed
                site can still be verified from its aerial + street view. */}
            <div className="grid md:grid-cols-2 gap-3">
              <ImageryCard
                title={mapProvider?.satellite_name || 'Satellite'}
                icon={<Satellite size={14} />}
                testid="site-insights-satellite"
              >
                <MapContainer
                  key={`sat-${target.site_id}`}
                  center={[target.lat, target.lng]}
                  zoom={18}
                  scrollWheelZoom={false}
                  zoomControl={false}
                  className="w-full h-full"
                  attributionControl={false}
                >
                  <TileLayer
                    url={mapProvider?.satellite_url || 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'}
                    maxZoom={mapProvider?.satellite_max_zoom || 19}
                    subdomains={mapProvider?.satellite_subdomains || []}
                  />
                  <CircleMarker
                    center={[target.lat, target.lng]}
                    radius={9}
                    pathOptions={{
                      color: BAND_COLOR[detail?.effective_band || target.band || 'Unknown'] || BAND_COLOR.Unknown,
                      fillColor: BAND_COLOR[detail?.effective_band || target.band || 'Unknown'] || BAND_COLOR.Unknown,
                      fillOpacity: 0.6, weight: 2,
                    }}
                  />
                </MapContainer>
              </ImageryCard>

              <ImageryCard
                title="Street View"
                icon={<Compass size={14} />}
                testid="site-insights-streetview"
                action={isGoogle && streetViewOk === 'ok' ? (
                  <a
                    href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${target.lat},${target.lng}`}
                    target="_blank" rel="noreferrer"
                    className="text-[11px] text-raptor-blue hover:underline inline-flex items-center gap-1"
                    data-testid="site-insights-streetview-open"
                  >
                    Open in Google Maps <ExternalLink size={11} />
                  </a>
                ) : null}
              >
                {!isGoogle ? (
                  <StreetViewFallback
                    heading="Google Maps not connected"
                    body="Enable Google Maps under Settings → Integrations to activate Street View for site verification."
                    icon={<Compass size={20} />}
                  />
                ) : streetViewOk === 'ok' ? (
                  <StreetViewPanorama lat={target.lat} lng={target.lng} label={target.site_name} />
                ) : streetViewOk === 'none' ? (
                  <StreetViewFallback
                    heading="No Street View coverage"
                    body="Google reports no roadside imagery within range of these coordinates."
                    icon={<Compass size={20} />}
                  />
                ) : streetViewOk === 'denied' ? (
                  <StreetViewFallback
                    heading="Street View unavailable"
                    body="Google rejected the request. Check that the Street View Static API is enabled, billing is active, and this site's domain is on the browser key's allowed-referrers list."
                    icon={<Compass size={20} />}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-raptor-navy/50">
                    <Loader2 size={16} className="animate-spin mr-2" /> Checking Street View…
                  </div>
                )}
              </ImageryCard>
            </div>

            {assessments.length === 0 ? (
              <div className="rounded-lg border border-dashed border-raptor-blue/40 bg-raptor-blue/5 px-4 py-6 text-center" data-testid="site-insights-unsurveyed">
                <MapPin className="mx-auto mb-2 text-raptor-blue/70" size={22} />
                <div className="font-headline text-sm text-raptor-navy">Site not yet surveyed</div>
                <div className="text-[11px] text-raptor-navy/60 mt-1 max-w-sm mx-auto">Aerial and street imagery are shown above for location verification. No risk assessment has been captured for this site yet.</div>
              </div>
            ) : (
              <>
            {/* Assessment breakdown ────────────────────── */}
            {detail && (
              <div className="rounded-lg border border-border/70 bg-white overflow-hidden" data-testid="site-insights-assessment">
                <div className="px-4 py-3 border-b border-border/70 bg-raptor-neutral/40 flex items-center justify-between">
                  <div className="flex items-center gap-2 font-headline text-sm text-raptor-navy">
                    <ShieldAlert size={16} className="text-raptor-blue" />
                    Risk assessment
                  </div>
                  <div className="text-[11px] text-raptor-navy/50 font-tabular">{detail.template_name} v{detail.template_version} · {detail.id}</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 px-4 py-3">
                  <Stat label="Computed score" value={detail.normalised_score?.toFixed(1) ?? '—'} />
                  <Stat
                    label="Effective score"
                    value={<span className={detail.is_overridden ? 'text-raptor-blue' : ''}>{detail.effective_score?.toFixed(1) ?? '—'}</span>}
                    hint={detail.is_overridden ? 'underwriter override' : undefined}
                  />
                  <Stat
                    label="Band"
                    value={<Badge style={{ backgroundColor: BAND_COLOR[detail.effective_band] || BAND_COLOR.Unknown, color: 'white' }}>{bandLabel(detail.effective_band)}</Badge>}
                  />
                  <Stat
                    label="ABM cash exposure"
                    value={abmCash?.cash_value_zar != null
                      ? <span className="inline-flex items-center gap-1"><Wallet size={12} className="text-raptor-navy/60" />R {abmCash.cash_value_zar.toLocaleString()}</span>
                      : '—'}
                    hint={abmCash?.resolved_model
                      ? `${abmCash.resolved_model}${typeof abmCash.match_confidence === 'number' ? ` · ${abmCash.match_confidence}% match` : ''}`
                      : 'no ABM data'}
                  />
                </div>

                {/* Factor table (compact) */}
                <div className="border-t border-border/70 max-h-64 overflow-y-auto">
                  <table className="w-full text-xs font-body">
                    <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70 sticky top-0">
                      <tr>
                        <th className="py-1.5 px-3 text-left w-[40%]">Question</th>
                        <th className="py-1.5 px-3 text-left w-[30%]">Answer</th>
                        <th className="py-1.5 px-3 text-right">Points</th>
                        <th className="py-1.5 px-3 text-right">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.factors || []).slice(0, 60).map((f, i) => (
                        <tr
                          key={`${f.question_code || f.factor_id || i}`}
                          className={`border-t border-border/40 ${f.is_abm_row ? 'bg-raptor-blue/5' : (f.passed === false ? 'bg-red-50/40' : '')}`}
                        >
                          <td className="py-1.5 px-3 text-raptor-navy" title={factorLabel(f)}>
                            <div className="line-clamp-2">{factorLabel(f)}</div>
                            {f.cluster && (
                              <div className="text-[9px] uppercase text-raptor-navy/40 tracking-wide">{f.cluster}</div>
                            )}
                          </td>
                          <td className="py-1.5 px-3 text-raptor-navy/80">{String(f.answer ?? '—')}</td>
                          <td className="py-1.5 px-3 text-right font-tabular">
                            {(f.awarded_points ?? f.contribution ?? 0)}/{f.max_points ?? f.weight ?? 0}
                          </td>
                          <td className="py-1.5 px-3 text-right font-tabular">{((f.weight ?? 0) * 100).toFixed(0)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Score trend (only when >= 2 assessments) ─── */}
            {trend.length >= 2 && (
              <div className="rounded-lg border border-border/70 bg-white p-4" data-testid="site-insights-trend">
                <div className="flex items-center gap-2 mb-2 font-headline text-sm text-raptor-navy">
                  <TrendingUp size={14} className="text-raptor-blue" /> Score trend for this site
                </div>
                <div style={{ width: '100%', height: 180 }}>
                  <ResponsiveContainer>
                    <LineChart data={trend} margin={{ top: 8, right: 16, bottom: 4, left: -12 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} domain={[0, 100]} />
                      <RTooltip />
                      <Line type="monotone" dataKey="score" stroke="#0EA5E9" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="text-[10px] text-raptor-navy/50 mt-1">{trend.length} inspection{trend.length === 1 ? '' : 's'} on record</div>
              </div>
            )}

            {/* Questionnaire answers ──────────────────── */}
            {detail && Object.keys(factorsByCategory).length > 0 && (
              <div className="rounded-lg border border-border/70 bg-white overflow-hidden" data-testid="site-insights-answers">
                <div className="px-4 py-3 border-b border-border/70 bg-raptor-neutral/40 font-headline text-sm text-raptor-navy">
                  Questionnaire answers
                </div>
                <div className="divide-y divide-border/40 max-h-96 overflow-y-auto">
                  {Object.entries(factorsByCategory).map(([cat, rows]) => (
                    <div key={cat} className="px-4 py-3">
                      <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60 mb-2">{cat}</div>
                      <dl className="space-y-2">
                        {rows.map((f, i) => (
                          <div
                            key={`${f.question_code || f.factor_id || i}`}
                            className={`grid grid-cols-1 md:grid-cols-5 gap-2 md:gap-3 text-xs items-start px-2 py-1.5 rounded ${f.passed === false ? 'bg-red-50/50' : ''}`}
                          >
                            <dt className="md:col-span-3 text-raptor-navy/80 leading-snug">
                              {factorLabel(f)}
                            </dt>
                            <dd className="md:col-span-2 md:text-right font-body font-medium text-raptor-navy">
                              <span className="inline-flex items-center gap-1.5">
                                {f.passed === false && <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500" aria-label="failed answer" />}
                                <span className="truncate">{String(f.answer ?? '—')}</span>
                              </span>
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Photos ─────────────────────────────────── */}
            <div className="rounded-lg border border-border/70 bg-white overflow-hidden" data-testid="site-insights-photos">
              <div className="px-4 py-3 border-b border-border/70 bg-raptor-neutral/40 flex items-center justify-between">
                <div className="flex items-center gap-2 font-headline text-sm text-raptor-navy">
                  <Camera size={14} className="text-raptor-blue" /> On-site photos
                </div>
                {photos.length > 0 && (
                  <div className="text-[11px] text-raptor-navy/60 font-tabular">{photoIdx + 1} / {photos.length}</div>
                )}
              </div>
              {photos.length === 0 ? (
                <div className="px-4 py-8 text-center text-xs text-raptor-navy/60" data-testid="site-insights-photos-empty">
                  <ImageOff className="mx-auto mb-2 opacity-40" size={24} />
                  Historic survey · no photos available.
                  <div className="mt-1 text-[10px] text-raptor-navy/40">
                    Future Raptor-native surveys captured on-device will display photos here.
                  </div>
                </div>
              ) : (
                <div className="relative bg-raptor-navy/5">
                  <div className="aspect-video w-full flex items-center justify-center overflow-hidden">
                    {activePhoto && !failedPhotos.has(activePhoto.id) ? (
                      <AuthedImage
                        key={activePhoto.id}
                        path={`/imports/photos/${activePhoto.id}/thumb`}
                        alt={`Site photo ${photoIdx + 1}`}
                        className="max-w-full max-h-full object-contain"
                        onError={() => setFailedPhotos((prev) => new Set(prev).add(activePhoto.id))}
                        testid={`site-insights-photo-${photoIdx}`}
                      />
                    ) : (
                      <div className="flex flex-col items-center gap-2 text-raptor-navy/50 text-xs" data-testid="site-insights-photo-unavailable">
                        <ImageOff size={24} className="opacity-40" />
                        Image unavailable
                      </div>
                    )}
                  </div>
                  {photos.length > 1 && (
                    <>
                      <button
                        type="button" onClick={prevPhoto} aria-label="Previous photo"
                        className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                        data-testid="site-insights-photo-prev"
                      ><ChevronLeft size={16} /></button>
                      <button
                        type="button" onClick={nextPhoto} aria-label="Next photo"
                        className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-full p-1.5 shadow"
                        data-testid="site-insights-photo-next"
                      ><ChevronRight size={16} /></button>
                      <div className="flex gap-1 justify-center py-2 bg-white border-t border-border/40 overflow-x-auto">
                        {photos.map((_, i) => (
                          <button
                            key={i} type="button" onClick={() => setPhotoIdx(i)}
                            aria-label={`Photo ${i + 1}`}
                            className={`h-1.5 w-6 rounded-full transition-colors ${i === photoIdx ? 'bg-raptor-blue' : 'bg-raptor-navy/20 hover:bg-raptor-navy/40'}`}
                            data-testid={`site-insights-photo-dot-${i}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── Small sub-components ─────────────────────────────────────────── */

const ImageryCard: React.FC<{
  title: string; icon: React.ReactNode; children: React.ReactNode; testid?: string;
  action?: React.ReactNode;
}> = ({ title, icon, children, testid, action }) => (
  <div className="rounded-lg border border-border/70 bg-white overflow-hidden" data-testid={testid}>
    <div className="px-3 py-2 border-b border-border/60 flex items-center justify-between bg-raptor-neutral/30">
      <div className="flex items-center gap-1.5 text-[11px] font-headline uppercase tracking-wide text-raptor-navy/70">
        {icon} {title}
      </div>
      {action}
    </div>
    <div className="aspect-video w-full bg-raptor-navy/5">{children}</div>
  </div>
);

const StreetViewFallback: React.FC<{
  heading: string; body: string; icon: React.ReactNode;
}> = ({ heading, body, icon }) => (
  <div className="w-full h-full flex flex-col items-center justify-center text-center px-4 py-6">
    <div className="text-raptor-navy/40 mb-2">{icon}</div>
    <div className="text-xs font-headline text-raptor-navy mb-1">{heading}</div>
    <div className="text-[11px] text-raptor-navy/60 max-w-xs">{body}</div>
  </div>
);

/**
 * Interactive Street View panorama, powered by the Maps JS SDK we
 * already lazy-load for autocomplete. Supports drag-to-rotate, pinch/
 * scroll-to-zoom, "walk down the street" via the roadside arrows, and
 * full-screen. Falls back gracefully to a loading spinner while the
 * SDK is still warming up.
 *
 * Why not an iframe embed? The JS Panorama gives us:
 *   • zero iframe chrome / border
 *   • the same "no imagery" surface the metadata probe uses (we hide
 *     the whole pane in that case via the parent's ``streetViewOk``)
 *   • native pinch-zoom on tablets
 *   • ability to set an initial POV that faces the site (heading = 0)
 *     — we can improve this later with reverse-heading calculation.
 */
const StreetViewPanorama: React.FC<{ lat: number; lng: number; label: string }> = ({ lat, lng, label }) => {
  const { ready } = useGoogleMaps();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!ready || !containerRef.current) return;
    let cancelled = false;
    const g = (window as unknown as {
      google?: { maps?: {
        importLibrary?: (name: string) => Promise<unknown>;
        LatLng: new (lat: number, lng: number) => unknown;
        geometry?: { spherical?: { computeHeading: (a: unknown, b: unknown) => number } };
      } };
    }).google;
    if (!g?.maps || typeof g.maps.importLibrary !== 'function') { setFailed(true); return; }
    const maps = g.maps;
    const importLib = g.maps.importLibrary;

    // Under loading=async the StreetView classes are NOT attached to the
    // global google.maps namespace — they only exist on the object
    // RETURNED by importLibrary('streetView'). Constructing from the
    // (undefined) globals is what made the FIRST open fail with Google's
    // "Could not connect to the server" overlay; the second open worked
    // only because the SDK had finished bootstrapping by then. We now
    // await the library and use its returned constructors.
    type StreetViewLib = {
      StreetViewService: new () => { getPanorama: (req: unknown, cb: (data: unknown, status: string) => void) => void };
      StreetViewPanorama: new (el: HTMLElement, opts: unknown) => unknown;
    };
    const init = async (): Promise<void> => {
      let lib: StreetViewLib;
      try {
        lib = (await importLib('streetView')) as StreetViewLib;
        try { await importLib('geometry'); } catch { /* heading stays 0 */ }
      } catch { setFailed(true); return; }
      if (cancelled || !containerRef.current) return;
      if (!lib?.StreetViewService || !lib?.StreetViewPanorama) { setFailed(true); return; }
      setFailed(false);
      // Confirm a panorama actually exists near these coords before we
      // instantiate — otherwise the SDK renders a blank/grey pane with no
      // error. On failure we surface a clean fallback instead.
      const svc = new lib.StreetViewService();
      svc.getPanorama({ location: { lat, lng }, radius: 60 }, (data: unknown, status: string) => {
        if (cancelled || !containerRef.current) return;
        if (status !== 'OK') { setFailed(true); return; }
        // Point the camera from the nearest pano toward the site centre.
        let heading = 0;
        try {
          const panoLoc = (data as { location?: { latLng?: unknown } })?.location?.latLng;
          const ch = maps.geometry?.spherical?.computeHeading;
          if (panoLoc && ch) heading = ch(panoLoc, new maps.LatLng(lat, lng));
        } catch { /* heading stays 0 */ }
        new lib.StreetViewPanorama(containerRef.current, {
          position: { lat, lng },
          pov: { heading, pitch: 0 },
          zoom: 1,
          addressControl: false,
          fullscreenControl: true,
          motionTracking: false,
          motionTrackingControl: false,
          linksControl: true,
          panControl: false,
          zoomControl: true,
          enableCloseButton: false,
        });
      });
    };
    void init();
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [ready, lat, lng]);

  if (!ready) {
    return (
      <div className="w-full h-full flex items-center justify-center text-raptor-navy/50">
        <Loader2 size={16} className="animate-spin mr-2" /> Loading Street View…
      </div>
    );
  }
  if (failed) {
    return (
      <StreetViewFallback
        heading="Street View couldn't load"
        body="The imagery exists but didn't render — usually the browser key's allowed-referrers list doesn't include this domain, or the Maps JavaScript API isn't enabled on the key."
        icon={<Compass size={20} />}
      />
    );
  }
  return (
    <div
      ref={containerRef}
      className="w-full h-full"
      data-testid="site-insights-streetview-panorama"
      aria-label={`Interactive Street View near ${label}`}
    />
  );
};

const Stat: React.FC<{
  label: string; value: React.ReactNode; hint?: string;
}> = ({ label, value, hint }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-raptor-navy/60">{label}</div>
    <div className="font-tabular font-semibold text-raptor-navy">{value}</div>
    {hint && <div className="text-[10px] text-raptor-navy/50 truncate">{hint}</div>}
  </div>
);

export default SiteInsightsModal;
